import React, { useState } from 'react';

interface PartsListProps {
  parts: any;
  theme?: 'light' | 'dark';
  showToggle?: boolean;
  searchTerm?: string;
  matchesWildcard?: (text: string, pattern: string) => boolean;
  forceSimpleList?: boolean;
}

const PartsList: React.FC<PartsListProps> = ({
  parts,
  theme = 'light',
  showToggle = false,
  searchTerm = '',
  matchesWildcard,
  forceSimpleList = false
}) => {
  const [showStructured, setShowStructured] = useState(false)
  const [expandedCitations, setExpandedCitations] = useState(new Set())
  const [showSimpleList, setShowSimpleList] = useState(false)
  
  const alphanumericSort = (a, b) => {
    const getNumericValue = (str) => {
      if (!str) return NaN
      
      // Handle ranges like "141 to 146" by taking the first number
      if (str.includes(' to ')) {
        const firstNumber = str.split(' to ')[0].trim()
        return parseInt(firstNumber, 10)
      }
      
      // Handle ranges like "141-146" by taking the first number
      if (str.includes('-') && /^\d+\s*-\s*\d+$/.test(str)) {
        const firstNumber = str.split('-')[0].trim()
        return parseInt(firstNumber, 10)
      }
      
      // Extract the first number from the string
      const match = str.match(/\d+/)
      return match ? parseInt(match[0], 10) : NaN
    }

    const numA = getNumericValue(a)
    const numB = getNumericValue(b)

    if (isNaN(numA) || isNaN(numB)) {
      return (a || '').localeCompare(b || '')
    }
    return numA - numB
  }

  // Helper function to get reference numerals, checking both capitalization formats
  const getReferenceNumerals = (part) => {
    if (!part) return []
    // First check Reference_Numerals (capital R), if it's empty, try reference_numerals (lowercase)
    let numerals = part.Reference_Numerals
    if (!numerals || (Array.isArray(numerals) && numerals.length === 0)) {
      numerals = part.reference_numerals
    }
    // Strip markdown ** formatting from numerals since they're already styled as bold
    if (Array.isArray(numerals)) {
      return numerals.map(numeral => 
        typeof numeral === 'string' ? numeral.replace(/\*\*/g, '') : numeral
      )
    }
    return []
  }
  
  // Helper function to get citations, checking both capitalization formats
  const getCitations = (part) => {
    if (!part) return []
    // Check both capitalizations - the API might return Citations (capital C)
    const citations = part.Citations || part.citations || []
    return Array.isArray(citations) ? citations : []
  }

  // Group parts by those with reference numerals vs those without, and filter by search term
  const groupedParts = React.useMemo(() => {
    if (!parts) return { withNumerals: [], withoutNumerals: [] }

    const withNumerals = []
    const withoutNumerals = []

    Object.keys(parts).forEach(key => {
      const part = parts[key]
      const partName = key.replace(/_/g, ' ')

      // Filter out "Unknown Components" entries (handles both underscore and space variations)
      const keyLower = key.toLowerCase().replace(/_/g, ' ');
      if (keyLower.includes('unknown component')) {
        return // Skip this part
      }

      // Apply search filter if provided
      if (searchTerm && matchesWildcard && !matchesWildcard(partName, searchTerm)) {
        return // Skip this part if it doesn't match the search
      }

      const referenceNumerals = getReferenceNumerals(part)
      if (referenceNumerals && referenceNumerals.length > 0) {
        withNumerals.push({ key, ...part })
      } else if (!key.startsWith('reference_numeral_')) {
        withoutNumerals.push({ key, ...part })
      }
    })
    
    // Sort parts with numerals by their first reference numeral
    withNumerals.sort((a, b) => {
      const refA = getReferenceNumerals(a)[0] || ''
      const refB = getReferenceNumerals(b)[0] || ''
      return alphanumericSort(refA, refB)
    })
    
    // Sort parts without numerals alphabetically
    withoutNumerals.sort((a, b) => {
      const nameA = a.key.replace(/_/g, ' ')
      const nameB = b.key.replace(/_/g, ' ')
      return nameA.localeCompare(nameB)
    })
    
    return { withNumerals, withoutNumerals }
  }, [parts, searchTerm, matchesWildcard])

  const formatPartName = (key) => {
    return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }

  const toggleCitationExpansion = (partKey) => {
    setExpandedCitations(prev => {
      const newSet = new Set(prev)
      if (newSet.has(partKey)) {
        newSet.delete(partKey)
      } else {
        newSet.add(partKey)
      }
      return newSet
    })
  }

  const formatCitations = (citations, partKey, isExpanded) => {
    if (!citations || citations.length === 0) return ''
    
    if (citations.length <= 5 || isExpanded) {
      const allCitations = citations.join(', ')
      if (citations.length > 5 && isExpanded) {
        return (
          <>
            {allCitations}{' '}
            <button
              onClick={() => toggleCitationExpansion(partKey)}
              className={`text-xs underline cursor-pointer hover:opacity-80 ${
                theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
              }`}
            >
              show less
            </button>
          </>
        )
      }
      return allCitations
    } else {
      const visibleCitations = citations.slice(0, 5).join(', ')
      return (
        <>
          {visibleCitations}{' '}
          <button
            onClick={() => toggleCitationExpansion(partKey)}
            className={`text-xs underline cursor-pointer hover:opacity-80 ${
              theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
            }`}
          >
            +{citations.length - 5} more
          </button>
        </>
      )
    }
  }

  const renderSimpleList = () => {
    // Check if search returned no results
    if (searchTerm && groupedParts.withNumerals.length === 0 && groupedParts.withoutNumerals.length === 0) {
      return (
        <div className={`text-center py-8 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
          <div className="text-lg mb-2">No parts found</div>
          <div className="text-sm">Try adjusting your search term or using wildcards (* for any characters)</div>
        </div>
      )
    }

    const allParts = [
      ...groupedParts.withNumerals.map(part => ({
        ...part,
        referenceNumerals: getReferenceNumerals(part)
      })),
      ...groupedParts.withoutNumerals.map(part => ({
        ...part,
        referenceNumerals: getReferenceNumerals(part)
      }))
    ]

    return (
      <div className="space-y-1">
        {allParts.map((part) => (
          <div key={part.key} className={`py-2 px-3 text-sm border-l-2 ${
            theme === 'dark' 
              ? 'border-gray-600 bg-gray-800 text-gray-200' 
              : 'border-gray-300 bg-gray-50 text-gray-900'
          }`}>
            <span className="font-medium">{formatPartName(part.key)}</span>
            {part.referenceNumerals && part.referenceNumerals.length > 0 && (
              <span className={`ml-2 font-bold ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}>
                {part.referenceNumerals.join(', ')}
              </span>
            )}
          </div>
        ))}
      </div>
    )
  }

  const renderFormattedParts = () => {
    // Check if search returned no results
    if (searchTerm && groupedParts.withNumerals.length === 0 && groupedParts.withoutNumerals.length === 0) {
      return (
        <div className={`text-center py-8 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
          <div className="text-lg mb-2">No parts found</div>
          <div className="text-sm">Try adjusting your search term or using wildcards (* for any characters)</div>
        </div>
      )
    }

    return (
      <div className="space-y-4">
        {/* Parts with Reference Numerals */}
        {groupedParts.withNumerals.length > 0 && (
          <div>
            <h4 className={`font-semibold mb-3 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              Parts with Reference Numerals
            </h4>
            <div className="space-y-2">
              {groupedParts.withNumerals.map((part) => {
                const referenceNumerals = getReferenceNumerals(part)
                const citations = getCitations(part)
                const isExpanded = expandedCitations.has(part.key)
                
                return (
                  <div key={part.key} className={`flex items-start gap-3 p-3 rounded-md ${
                    theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
                  }`}>
                    <div className="flex-1">
                      <div className={`font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}>
                        {formatPartName(part.key)}
                        <span className={`ml-2 font-bold text-lg ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}>
                          {referenceNumerals.join(', ')}
                        </span>
                      </div>
                      {(part.Description || part.description) && (
                        <div className={`text-sm mt-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                          <span className="font-medium">Description: </span>
                          {part.Description || part.description}
                        </div>
                      )}
                      {(part.Details || part.details) && (
                        <div className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                          <span className="font-medium">Details: </span>
                          {part.Details || part.details}
                        </div>
                      )}
                      {citations && citations.length > 0 && (
                        <div className={`text-xs mt-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                          Citations: {formatCitations(citations, part.key, isExpanded)}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}
        
        {/* Parts without Reference Numerals */}
        {groupedParts.withoutNumerals.length > 0 && (
          <div>
            <h4 className={`font-semibold mb-3 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              Parts without Reference Numerals
            </h4>
            <div className="space-y-2">
              {groupedParts.withoutNumerals.map((part) => {
                const citations = getCitations(part)
                const isExpanded = expandedCitations.has(part.key)
                
                return (
                  <div key={part.key} className={`flex items-start gap-3 p-3 rounded-md ${
                    theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
                  }`}>
                    <div className="flex-1">
                      <div className={`font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}>
                        {formatPartName(part.key)}
                      </div>
                      {(part.Description || part.description) && (
                        <div className={`text-sm mt-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                          <span className="font-medium">Description: </span>
                          {part.Description || part.description}
                        </div>
                      )}
                      {(part.Details || part.details) && (
                        <div className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                          <span className="font-medium">Details: </span>
                          {part.Details || part.details}
                        </div>
                      )}
                      {citations && citations.length > 0 && (
                        <div className={`text-xs mt-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                          Citations: {formatCitations(citations, part.key, isExpanded)}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    )
  }

  const renderStructuredData = () => {
    return (
      <pre className={`text-sm whitespace-pre-wrap font-mono p-4 rounded-md ${
        theme === 'dark' ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800'
      }`}>
        {JSON.stringify(parts, null, 2)}
      </pre>
    )
  }

  if (!parts) {
    return (
      <div className={`p-6 rounded-lg text-center ${
        theme === 'dark' ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-500'
      }`}>
        No parts data available
      </div>
    )
  }

  return (
    <div className={`p-6 rounded-lg ${
      theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'
    }`}>
      {showToggle && (
        <div className="mb-4 flex items-center gap-2">
          <button
            onClick={() => {
              setShowStructured(!showStructured)
              if (showSimpleList) setShowSimpleList(false)
            }}
            className={`text-xs py-1 px-3 rounded-md transition-colors ${
              showStructured
                ? theme === 'dark' ? 'bg-gray-600 text-gray-200' : 'bg-gray-300 text-gray-700'
                : theme === 'dark' ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'
            }`}
          >
            {showStructured ? 'Show Formatted View' : 'Show Raw Data'}
          </button>
          <button
            onClick={() => {
              setShowSimpleList(!showSimpleList)
              if (showStructured) setShowStructured(false)
            }}
            className={`text-xs py-1 px-3 rounded-md transition-colors ${
              showSimpleList
                ? theme === 'dark' ? 'bg-gray-600 text-gray-200' : 'bg-gray-300 text-gray-700'
                : theme === 'dark' ? 'bg-green-600 text-white' : 'bg-green-500 text-white'
            }`}
          >
            {showSimpleList ? 'Show Detailed View' : 'View Simple List'}
          </button>
        </div>
      )}

      <div className="max-h-[calc(100vh-270px)] overflow-y-auto">
        {showStructured ? renderStructuredData() : (showSimpleList || forceSimpleList) ? renderSimpleList() : renderFormattedParts()}
      </div>
    </div>
  )
}

export default PartsList
